import { Tab, TabProps } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { LearnerRoutes } from "@outschool/routes";
import { Loading } from "@outschool/ui-legacy-component-library";
import { Redirect } from "@outschool/ui-utils";
import { useRoutes } from "@patched/hookrouter";
import React from "react";

import Link from "../components/Link";
import { useLearnerClassroomContext } from "../providers/LearnerClassroomProvider";
import ClassroomHeader from "../routes/LearnerClassroom/ClassroomHeader";
import {
  classroomRoutes,
  tabValues,
} from "../routes/LearnerClassroom/classroomRoutes";
import { NotFoundPage } from "../routes/NotFoundPage";
import TabbedLayout from "./TabbedLayout";

const useTabs = ({
  sectionUid,
  hasLessons,
  hasAssignments,
  isSelfPaced,
  isFlexMigratedSelfPaced,
}: {
  sectionUid: string;
  hasLessons: boolean;
  hasAssignments: boolean;
  isSelfPaced?: boolean;
  isFlexMigratedSelfPaced?: boolean;
}): TabProps[] => {
  const { t } = useTranslation("learnerApp\\LearnerClassroom");
  return [
    ...(isSelfPaced
      ? []
      : [
          {
            value: tabValues.Classroom,
            label: t("Classroom"),
            component: Link,
            to: LearnerRoutes.classroomPath(sectionUid),
          } as TabProps<typeof Link>,
        ]),
    ...(!isFlexMigratedSelfPaced && hasLessons
      ? [
          {
            value: tabValues.Lessons,
            label: t("Lessons"),
            component: Link,
            to: LearnerRoutes.lessonsPath(sectionUid),
          } as TabProps<typeof Link>,
        ]
      : []),
    ...(hasAssignments
      ? [
          {
            value: tabValues.Assignments,
            label: t("Assignments"),
            component: Link,
            to: LearnerRoutes.assignmentsPath(sectionUid),
          } as TabProps<typeof Link>,
        ]
      : []),
    {
      value: tabValues.PrivateClassMessages,
      label: t("Message Teacher"),
      component: Link,
      to: LearnerRoutes.privateClassMessagesPath(sectionUid),
    } as TabProps<typeof Link>,
  ];
};

const LearnerClassroomTabbedLayout = () => {
  const {
    loading,
    activity,
    classroom,
    sectionUid,
    section,
    hasLessons,
    hasAssignments,
  } = useLearnerClassroomContext();

  const isFlexMigratedSelfPaced =
    activity?.isSelfPaced && !section?.is_self_paced;
  const tab = useRoutes(classroomRoutes);
  const tabs = useTabs({
    sectionUid,
    hasLessons,
    hasAssignments,
    isSelfPaced: section?.is_self_paced,
    isFlexMigratedSelfPaced,
  });

  if (loading) {
    return <Loading />;
  }

  if (!tab || !classroom) {
    return <NotFoundPage />;
  }

  const { value, renderPage } = tab;

  // Self paced classes (excluding sections migrated from flex) do not have
  // 'Classroom' tab and should redirect to the lessons tab on root path.
  if (section?.is_self_paced && value === tabValues.Classroom) {
    return <Redirect to={LearnerRoutes.lessonsPath(sectionUid)} />;
  }

  return (
    <TabbedLayout
      currentTab={value ?? false}
      header={<ClassroomHeader />}
      tabs={tabs.map(tab => (
        <Tab key={tab.value} {...tab} />
      ))}
    >
      {renderPage()}
    </TabbedLayout>
  );
};

export default LearnerClassroomTabbedLayout;
